import React, { useEffect, useState } from "react";
import { X, Download, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

/**
 * MediaLightbox — Vollbild-Viewer für Bilder + Inline-Video-Player.
 *
 * Props:
 *   open: bool
 *   items: [{ id, filename_original, mime_type, media_type, inlineUrl, downloadUrl }]
 *   index: number — aktueller Index
 *   onClose, onPrev, onNext
 */
export default function MediaLightbox({ open, items, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") onPrev?.();
      else if (e.key === "ArrowRight") onNext?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, onPrev, onNext]);

  if (!open || !items?.length) return null;
  const item = items[index];
  if (!item) return null;

  const isVideo = item.media_type === "video" || (item.mime_type || "").startsWith("video/");
  const total = items.length;

  return (
    <div
      data-testid="media-lightbox"
      className="fixed inset-0 z-[100] bg-navy/95 flex flex-col"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      {/* Top Bar */}
      <header className="flex items-center justify-between gap-4 px-4 py-3 border-b border-white/10 text-white">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widish font-sans text-white/55">
            Newton Campus Garching · Datenraum
          </div>
          <div className="font-display text-base sm:text-lg truncate" title={item.filename_original}>
            {item.filename_original}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-sans text-white/55 tabular-nums">
            {index + 1} / {total}
          </span>
          {item.downloadUrl && (
            <a
              href={item.downloadUrl}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-sans text-white/85 border border-white/20 hover:bg-white/10"
              data-testid="lightbox-download"
            >
              <Download size={12} /> Original
            </a>
          )}
          <button
            onClick={onClose}
            className="p-1.5 text-white/85 hover:text-white border border-white/20 hover:bg-white/10"
            data-testid="lightbox-close"
            aria-label="Schließen"
          >
            <X size={16} />
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        {/* Prev/Next */}
        {total > 1 && (
          <>
            <button
              onClick={onPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 text-white/70 hover:text-white border border-white/20 hover:bg-white/10"
              data-testid="lightbox-prev"
              aria-label="Vorheriges"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={onNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white/70 hover:text-white border border-white/20 hover:bg-white/10"
              data-testid="lightbox-next"
              aria-label="Nächstes"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}

        {/* Media */}
        {isVideo ? (
          <video
            key={item.id}
            src={item.inlineUrl}
            controls
            autoPlay
            className="max-w-[92vw] max-h-[80vh] bg-black"
            data-testid="lightbox-video"
          >
            Ihr Browser unterstützt kein Video-Element.
          </video>
        ) : (
          <img
            key={item.id}
            src={item.inlineUrl}
            alt={item.filename_original}
            className="max-w-[92vw] max-h-[80vh] object-contain"
            data-testid="lightbox-image"
          />
        )}
      </div>

      {/* Bottom Info */}
      <footer className="px-4 py-2 text-xs font-sans text-white/55 border-t border-white/10 flex items-center gap-3">
        <ZoomIn size={12} />
        <span className="hidden sm:inline">ESC schließt · ← → wechseln</span>
        <span className="ml-auto">{item.kategorie_label || item.kategorie}</span>
      </footer>
    </div>
  );
}
